import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Calendar, Users, Star, ChevronRight, Heart, Clock, MapPin, X, MessageCircle, ArrowUpDown } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getMyCreatedEvents, getMyAttendingEvents, getInterestedEvents, upvoteEvent } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

export default function MyEventsPage() {
  const [createdEvents, setCreatedEvents] = useState([]);
  const [attendingEvents, setAttendingEvents] = useState([]);
  const [interestedEvents, setInterestedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interestedSort, setInterestedSort] = useState('upcoming');
  const navigate = useNavigate();

  const loadEvents = useCallback(async () => {
    try {
      const [created, attending] = await Promise.all([
        getMyCreatedEvents(),
        getMyAttendingEvents()
      ]);
      setCreatedEvents(created.data);
      setAttendingEvents(attending.data);
    } catch (error) {
      toast.error('Failed to load events');
    }
    setLoading(false);
  }, []);

  const loadInterestedEvents = useCallback(async () => {
    try {
      const res = await getInterestedEvents(interestedSort);
      setInterestedEvents(res.data);
    } catch (error) {
      console.error('Failed to load interested events');
    }
  }, [interestedSort]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadInterestedEvents();
  }, [loadInterestedEvents]);

  const handleRemoveInterested = async (eventId) => {
    try {
      await upvoteEvent(eventId); // Toggle off
      setInterestedEvents(prev => prev.filter(e => e.event_id !== eventId));
      toast('Removed from interested');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const EventCard = ({ event, showStats }) => (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`event-card-${event.event_id}`}
      className="w-full flex gap-4 p-4 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all text-left"
      onClick={() => navigate(`/event/${event.event_id}`)}
    >
      <img
        src={event.image_url}
        alt={event.title}
        className="w-20 h-20 rounded-lg object-cover"
      />
      <div className="flex-1 min-w-0">
        <Badge className="mb-1 text-xs bg-gray-100 text-gray-600 border-0">
          {event.category}
        </Badge>
        <h3 className="font-semibold truncate text-gray-800">{event.title}</h3>
        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
          <Calendar className="w-3 h-3" />
          <span>{formatDate(event.event_date)}</span>
        </div>
        {showStats && (
          <div className="flex items-center gap-3 mt-2 text-sm">
            <span className="flex items-center gap-1 text-gray-500">
              <Users className="w-3 h-3" />
              {event.chat_members}
            </span>
            {event.average_rating > 0 && (
              <span className="flex items-center gap-1 text-gray-500">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                {event.average_rating.toFixed(1)}
              </span>
            )}
            <Badge className={`text-xs ${event.fee > 0 ? 'bg-pink-100 text-pink-600' : 'bg-green-100 text-green-600'} border-0`}>
              {event.fee > 0 ? `£${event.fee.toFixed(2)}` : 'Free'}
            </Badge>
          </div>
        )}
      </div>
      <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0 self-center" />
    </motion.button>
  );

  const InterestedEventCard = ({ event, onRemove }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
    >
      <div className="flex">
        <img
          src={event.image_url}
          alt={event.title}
          className="w-28 h-28 object-cover cursor-pointer"
          onClick={() => navigate(`/event/${event.event_id}`)}
        />
        <div className="flex-1 p-3 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 
              className="font-semibold text-gray-800 truncate cursor-pointer hover:text-pink-500"
              onClick={() => navigate(`/event/${event.event_id}`)}
            >
              {event.title}
            </h3>
            <Badge className="bg-pink-100 text-pink-600 border-0 text-xs flex-shrink-0">
              💖 Interested
            </Badge>
          </div>
          
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <Clock className="w-3 h-3" />
            <span>{formatDate(event.event_date)}</span>
          </div>
          
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{event.location}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-7 px-3 bg-blue-500 hover:bg-blue-600 text-xs rounded-lg"
              onClick={() => navigate(`/chat/${event.event_id}`)}
            >
              <MessageCircle className="w-3 h-3 mr-1" />
              Chat
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-3 text-xs rounded-lg border-gray-200 text-gray-500"
              onClick={() => onRemove(event.event_id)}
            >
              <X className="w-3 h-3 mr-1" />
              Remove
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 ml-auto"
              onClick={() => navigate(`/event/${event.event_id}`)}
            >
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-pink-500 animate-pulse mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="flex items-center justify-between p-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">My Events</h1>
            <p className="text-sm text-gray-500">Events you&apos;ve created & saved</p>
          </div>
          <Button 
            data-testid="create-event-btn-header"
            size="icon" 
            className="rounded-full bg-pink-500 hover:bg-pink-600"
            onClick={() => navigate('/create-event')}
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <Tabs defaultValue="interested" className="p-4">
        <TabsList className="w-full grid grid-cols-3 bg-gray-100 rounded-xl p-1">
          <TabsTrigger 
            value="interested" 
            data-testid="interested-tab"
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-pink-500"
          >
            <Heart className="w-4 h-4 mr-1" />
            Interested
          </TabsTrigger>
          <TabsTrigger 
            value="created" 
            data-testid="created-tab"
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-pink-500"
          >
            Created
          </TabsTrigger>
          <TabsTrigger 
            value="attending" 
            data-testid="attending-tab"
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-pink-500"
          >
            Joined
          </TabsTrigger>
        </TabsList>

        {/* Interested Tab */}
        <TabsContent value="interested" className="mt-4 space-y-3">
          {/* Sort Options */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">{interestedEvents.length} events</span>
            <Select value={interestedSort} onValueChange={setInterestedSort}>
              <SelectTrigger className="w-40 h-9 rounded-lg border-gray-200" data-testid="interested-sort">
                <ArrowUpDown className="w-3 h-3 mr-2 text-gray-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">Upcoming first</SelectItem>
                <SelectItem value="recent">Recently added</SelectItem>
                <SelectItem value="category">By category</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {interestedEvents.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-6">
                <Heart className="w-10 h-10 text-pink-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No interested events yet</h3>
              <p className="text-gray-500 mb-6 max-w-xs mx-auto">
                Swipe right or tap the heart on events you like to save them here
              </p>
              <Button 
                onClick={() => navigate('/feed')}
                className="bg-pink-500 hover:bg-pink-600 rounded-xl"
              >
                Discover Events
              </Button>
            </div>
          ) : (
            interestedEvents.map((event, idx) => (
              <InterestedEventCard 
                key={event.event_id} 
                event={event} 
                onRemove={handleRemoveInterested}
              />
            ))
          )}
        </TabsContent>

        {/* Created Tab */}
        <TabsContent value="created" className="mt-4 space-y-3">
          {createdEvents.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No events created yet</h3>
              <p className="text-gray-500 mb-6">Create your first event and share it with others!</p>
              <Button 
                onClick={() => navigate('/create-event')}
                className="bg-pink-500 hover:bg-pink-600 rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </div>
          ) : (
            createdEvents.map(event => (
              <EventCard key={event.event_id} event={event} showStats />
            ))
          )}
        </TabsContent>

        {/* Attending/Joined Tab */}
        <TabsContent value="attending" className="mt-4 space-y-3">
          {attendingEvents.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <MessageCircle className="w-10 h-10 text-blue-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No events joined yet</h3>
              <p className="text-gray-500 mb-6">Join event chats to see them here!</p>
              <Button 
                onClick={() => navigate('/feed')}
                className="bg-pink-500 hover:bg-pink-600 rounded-xl"
              >
                Discover Events
              </Button>
            </div>
          ) : (
            attendingEvents.map(event => (
              <EventCard key={event.event_id} event={event} />
            ))
          )}
        </TabsContent>
      </Tabs>

      <BottomNav />
    </div>
  );
}
